import { LocalDate } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dyn } from "trader.ts/ui";
import {
  constantField,
  dateBox,
  Form,
  selectBox,
  textBox,
} from "trader.ts/ui.forms";
import {
  phone_type,
  studentemailid,
  studentid,
  studentphoneid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { makeArrayField, oneLineArrayRender } from "../../client/arrayfield";
import { renderCancelButton, renderSaveButton } from "../../client/savebutton";
import {
  allPhoneTypes,
  parsePhoneType,
  phoneType,
} from "../../parsing_from_ur";
import { ddMMMMyyyy, renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";
import { translatePhoneType } from "../../translations_specific";
import { emailValidation } from "../../lib/emailisvalid";
import { makeEmailSecret } from "../../lib/emailsecret";

type student = {
  id: studentid;
  firstname: string;
  lastname: string;
  vat: string;
  birthday: LocalDate | null;
  street: string;
  number: string;
  bus: string;
  zip: string;
  city: string;
  emails:
    | {
        id: studentemailid;
        email: string;
      }[]
    | null;
  phones:
    | {
        id: studentphoneid;
        number: string;
        description: phone_type | null;
      }[]
    | null;
};

export function render(opts: {
  students: student[];
}): HTMLElement | HTMLElement[] {
  const studentsRendered = opts.students
    .orderBy(renderFirstNameLastName)
    .map((student) => {
      const editingS = new Source(false);

      return dyn(editingS, (editing) => {
        if (editing) {
          const form = studentForm(student);
          return (
            <div
              className={style.card_large + style.card + style.card_accent}
              style="border-top-color: var(--grey-700);"
            >
              <div>
                <b>{renderFirstNameLastName(student)}</b>
              </div>
              {form.render()}
              <div style="text-align: right;">
                {renderCancelButton({ onclick: () => editingS.set(false) })}
                {renderSaveButton({
                  formS: form.s,
                  save: async (formValues) => {
                    await rpc(routes.saveStudentProfileSpec, undefined, {
                      ...formValues,
                      id: student.id,
                      phones: formValues.phones.map((ph) => ({
                        ...ph,
                        description:
                          ph.description === "none" ? null : ph.description,
                      })),
                    });
                    editingS.set(false);
                  },
                  timeout: 1,
                  reload: true,
                })}
              </div>
            </div>
          );
        } else {
          return (
            <div className={style.card_large + style.card + style.card_accent}>
              <div style="display: flex;">
                <div style="flex: 1">{renderStudent(student)}</div>
                <div>
                  <button
                    className={
                      style.button_has_only_icon +
                      style.button_round +
                      style.button_em_down
                    }
                    onclick={() => editingS.set(true)}
                  >
                    <i className={FA.icon_pencil_alt_solid}></i>
                  </button>
                </div>
              </div>
            </div>
          );
        }
      });
    });

  return (
    <div>
      <article
        className={style.flex_responsive}
        style="justify-content: flex-end; margin: 16px 0px 32px;"
      >
        <a
          className={style.button + style.text_with_icon}
          href={routes.reset_passwordRoute.link()}
        >
          <i className={FA.icon_question_solid}></i>
          <div>{"Verander je wachtwoord"}</div>
        </a>
        <a
          className={style.button_accent + style.text_with_icon}
          href={routes.logoutRoute.link({ redirecturl: undefined })}
          style="margin: 0px;"
        >
          <i className={FA.icon_user_solid}></i>
          <div>{"Log uit"}</div>
        </a>
      </article>

      <label>{"Studentgegevens"}</label>
      {studentsRendered}
    </div>
  );
}

function renderStudent(student: student): HTMLElement | HTMLElement[] {
  return (
    <div>
      <div>
        <b>{renderFirstNameLastName(student)}</b>
      </div>
      <article>
        <div className={style.text_with_icon}>
          <i className={FA.icon_envelope_solid}></i>
          <div>
            {(student.emails || []).map((em) => (
              <div>
                {em.email}{" "}
                <a
                  href={routes.emailsettingsRoute.link({
                    emailaddress: em.email,
                    secret: makeEmailSecret(em.email),
                  })}
                >
                  ({"Emailinstellingen"})
                </a>
              </div>
            ))}
          </div>
        </div>
        <article></article>
        <div className={style.text_with_icon}>
          <i className={FA.icon_phone_solid}></i>
          <div>
            {(student.phones || []).map((p) => (
              <div>{p.number}</div>
            ))}
          </div>
        </div>
      </article>

      {student.birthday ? (
        <article className={style.text_with_icon}>
          <i className={FA.icon_birthday_cake}></i>
          <div>{ddMMMMyyyy(student.birthday)}</div>
        </article>
      ) : (
        <span></span>
      )}
      {student.street ||
      student.number ||
      student.bus ||
      student.zip ||
      student.city ? (
        <div className={style.text_with_icon}>
          <i className={FA.icon_house}></i>
          <div>
            <div>
              {student.street} {student.number} {student.bus}
            </div>
            <div>
              {student.zip} {student.city}
            </div>
          </div>
        </div>
      ) : (
        []
      )}
    </div>
  );
}

function studentForm(student: student) {
  return new Form({})
    .addField("emails", [], async () =>
      makeArrayField({
        initialVal: (student.emails || []).map((em) => ({
          email: em.email,
          id: em.id as studentemailid | null,
        })),
        makeSubForm: async function (t: {
          email: string;
          id: studentemailid | null;
        }) {
          return new Form({})
            .addField("id", [], () => constantField(t.id))
            .addField("email", [], () =>
              textBox({ initialVal: t.email, validations: [emailValidation] }),
            )
            .build({ render: (fields) => fields.email() });
        },
        makeNew: () => ({ email: "", id: null }),
        render: oneLineArrayRender(function rowIsEmpty(row: { email: string }) {
          return row.email.trim().length === 0;
        }),
      }),
    )
    .addField("phones", [], async () =>
      makeArrayField({
        initialVal: (student.phones || []).map((p) => ({
          number: p.number,
          description: p.description
            ? parsePhoneType(p.description)
            : ("none" as const),
          id: p.id as studentphoneid | null,
        })),
        makeSubForm: async function (p) {
          return new Form({})
            .addField("id", [], () => constantField(p.id))
            .addField("number", [], () =>
              textBox({ type: "tel", initialVal: p.number }),
            )
            .addField("description", [], () =>
              selectBox<"none" | phoneType>({
                values: ["none", ...allPhoneTypes],
                initial: p.description,
                show: (d) => (d === "none" ? "" : translatePhoneType(d)),
              }),
            )
            .build({
              render: (fields) => {
                return (
                  <div className={style.equal_columns_non_responsive}>
                    {fields.number()}
                    {fields.description()}
                  </div>
                );
              },
            });
        },
        makeNew: () => ({ number: "", description: "none" as const, id: null }),
        render: oneLineArrayRender(function rowIsEmpty(row: {
          number: string;
        }) {
          return row.number.trim().length === 0;
        }),
      }),
    )
    .addField("birthday", [], () =>
      dateBox({
        initialVal: student.birthday || undefined,
        label: "Geboortedatum",
      }),
    )
    .addField("vat", [], () =>
      textBox({
        initialVal: student.vat || "",
        label: "BTW nummer",
      }),
    )
    .addField("street", [], () =>
      textBox({ initialVal: student.street, label: "Straat" }),
    )
    .addField("number", [], () =>
      textBox({ initialVal: student.number, label: "Huisnummer" }),
    )
    .addField("bus", [], () =>
      textBox({ initialVal: student.bus, label: "Bus" }),
    )
    .addField("zip", [], () =>
      textBox({ initialVal: student.zip, label: "postcode" }),
    )
    .addField("city", [], () =>
      textBox({ initialVal: student.city, label: "Stad" }),
    )
    .build({
      render: (fields) => {
        return (
          <div>
            <div>
              <label>{"Email"}</label>
              {fields.emails()}
            </div>
            <div>
              <label>{"Telefoon"}</label>
              {fields.phones()}
            </div>
            <div className={style.equal_columns}>
              {fields.birthday()}
              {fields.vat()}
            </div>
            <div className={style.equal_columns}>
              {fields.street()}
              <div className={style.equal_columns_non_responsive}>
                {fields.number()}
                {fields.bus()}
              </div>
            </div>
            <div className={style.equal_columns}>
              {fields.zip()}
              {fields.city()}
            </div>
          </div>
        );
      },
    });
}
